import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import {
  getUserSnackOrdersOptions,
  snackSupplierOptions,
  useBuilding,
} from "@/api";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import Table from "@/components/table/Table";
import { NOK } from "@/lib";
import { IntercomEvent, SnackOrder } from "@/types";
import { Button, ProgressIndicator } from "@app-components";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  snackStatusColorMap,
  snackStatusSortMap,
  snackOrderStatusStep,
  snackOrderStatusKey,
  snackOrderIsTerminal,
} from "@/lib/snackOrderLifecycle";
import { OrderReadError } from "@/components/snack/OrderReadError";
import { useCommonSnackTableColumns } from "@/hooks/useCommonSnackTableColumns";
import ContactDetails from "@/components/snack/ContactDetails";
import useSnackOrderStates from "@/hooks/useSnackOrderStates";
import { trackEvent } from "@intercom/messenger-js-sdk";
import { useLoggedInUser, useRouterStore } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";

const columnHelper = createColumnHelper<SnackOrder>();

export const Route = createFileRoute("/_portal/orders/")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(snackSupplierOptions(supplierCompanyId));
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: building } = useBuilding();
  const currUser = useLoggedInUser();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const {
    data: orders,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useBuildingQuery(
    getUserSnackOrdersOptions(
      currUser.userId,
      currUser.buildingTenantId,
      supplier?.lifecycleMode,
    ),
  );
  const { resetOrderStates } = useSnackOrderStates();

  const anyOrdersAreUnfinished = useMemo(
    () =>
      !!orders?.some((order) =>
        order.lifecycleMode === "delivery-closure-v1"
          ? !snackOrderIsTerminal(order.status.current)
          : snackOrderStatusStep(order.status.current, order.lifecycleMode) < 3,
      ),
    [orders],
  );

  const { deliveryTimeColumn, locationColumn } = useCommonSnackTableColumns();
  const columns = useMemo(
    () => [
      columnHelper.accessor("orderNumber", {
        cell: (info) => "#" + info.getValue(),
        header: t("PAGES.MY_ORDERS.TABLE.ORDER_NUMBER"),
        meta: { label: t("PAGES.MY_ORDERS.TABLE.ORDER_NUMBER") },
      }),
      deliveryTimeColumn,
      locationColumn,
      columnHelper.accessor("totalAmount", {
        cell: (info) => NOK.format(info.getValue()),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("PAGES.MY_ORDERS.TABLE.TOTAL")}
          />
        ),
        meta: {
          label: t("PAGES.MY_ORDERS.TABLE.TOTAL"),
          style: {
            textAlign: "right",
            display: "flex",
            justifyContent: "end",
            paddingRight: "var(--wlcm-spacing-lg)",
          },
        },
      }),
      columnHelper.accessor("status.current", {
        cell: (info) => {
          const status = info.getValue();
          if (!status) return null;
          return (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--wlcm-spacing-sm)",
              }}
            >
              {snackOrderIsTerminal(status) ? (
                <div style={anyOrdersAreUnfinished ? { width: 60 } : {}}>
                  <div
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 100,
                      background: snackStatusColorMap[status],
                      ...(anyOrdersAreUnfinished
                        ? {
                            marginLeft: "auto",
                          }
                        : {}),
                    }}
                  ></div>
                </div>
              ) : (
                <div style={{ width: 60 }}>
                  <ProgressIndicator
                    id={`status-${info.row.id}`}
                    label="Status"
                    hideLabel
                    total={
                      info.row.original.lifecycleMode === "delivery-closure-v1"
                        ? 5
                        : 4
                    }
                    value={snackOrderStatusStep(
                      status,
                      info.row.original.lifecycleMode,
                    )}
                    barColor={snackStatusColorMap[status]}
                    trackHeight={10}
                    trackColor="rgba(0,0,0,0.1)"
                  />
                </div>
              )}
              <strong style={{ color: snackStatusColorMap[status] }}>
                {t(
                  snackOrderStatusKey(
                    status,
                    info.row.original.lifecycleMode,
                    "ORDERED",
                  ),
                )}
              </strong>
              {info.row.original.lifecycleDiagnostic && (
                <span role="alert">
                  {t("PAGES.MY_ORDERS.LIFECYCLE_DATA_UNAVAILABLE")}
                </span>
              )}
            </span>
          );
        },
        header: (info) => <SortableTableHeader info={info} label="Status" />,
        sortingFn: (rowA, rowB) => {
          return (
            snackStatusSortMap[rowA.original.status.current] -
            snackStatusSortMap[rowB.original.status.current]
          );
        },
        meta: { label: "Status" },
      }),
    ],
    [t, deliveryTimeColumn, locationColumn, anyOrdersAreUnfinished],
  );

  return (
    <>
      <PageBreadcrumbs
        levels={[{ label: t("PAGES.MY_ORDERS.HEADER"), path: "/orders" }]}
      />
      <div className="pageHeader">
        <h1>{t("PAGES.MY_ORDERS.HEADER")}</h1>
        <p>
          {t("PAGES.MY_ORDERS.SUBHEADER", {
            buildingName: building.displayName,
          })}
          {(supplier?.email || supplier?.phone) && (
            <>
              {" "}
              <ContactDetails supplier={supplier} />
            </>
          )}
        </p>
      </div>
      <Button
        icon="add"
        onClick={() => {
          trackEvent(IntercomEvent.STARTED_SNACK_ORDER, {
            from_orders_page: true,
          });
          resetOrderStates();
          navigate({ to: "/orders/new" });
        }}
        style={{ marginLeft: "auto" }}
      >
        {t("SIDEBAR.ORDER")}
      </Button>
      {!!error && (
        <OrderReadError
          retry={refetch}
          isFetching={isFetching}
          hasData={!!orders}
          hasDraft={false}
        />
      )}
      {(!error || orders) && (
        <Table
          name="user-orders"
          columns={columns}
          data={orders}
          isLoading={isLoading}
          onRowClick={(order) => navigate({ to: `/orders/${order.id}` })}
          onRowHover={(order) => {
            router.preloadRoute({
              to: "/orders/$orderId",
              params: { orderId: order.id },
            });
          }}
          defaultSorting={[
            {
              id: "deliveryTime",
              desc: true,
            },
          ]}
          noDataMessage={t("PAGES.MY_ORDERS.TABLE.NO_DATA")}
        />
      )}
    </>
  );
}
